// Custom packages
import { isArchiveV3Live } from "src/app/shared/utils/archive-switch.utils";

/**
 * The entries of the legacy pages that lead to the 3.0.0 archive once the switch is live
 * (@see isArchiveV3Live). Until then every entry keeps leading where it leads today.
 *
 * Kept apart from the pages so that the switch-time behaviour is written once and tested once, and
 * so that, after the switch, the legacy branches go away together (FEX-28).
 */

/** A Mongo id: the only shape a property id can take in an address */
const OBJECT_ID_PATTERN = /^[0-9a-fA-F]{24}$/;

function isRecord(value: unknown): value is { [key: string]: unknown } {
  return typeof value === "object" && value !== null;
}

/**
 * The property of a shared role: the first of `propertiesPermission.property`, as the legacy
 * `my-property` page reads it. Accepts an id or an object carrying `_id`; anything that is not a
 * Mongo id gives "".
 *
 * @since 3.0.0
 */
export function sharedRolePropertyId(role: unknown): string {
  const permission =
    isRecord(role) && isRecord(role.propertiesPermission) ? role.propertiesPermission : null;
  const properties: unknown[] =
    permission && Array.isArray(permission.property) ? permission.property : [];
  const first: unknown = properties.length ? properties[0] : undefined;
  const id: unknown = isRecord(first) ? first._id : first;
  return typeof id === "string" && OBJECT_ID_PATTERN.test(id) ? id : "";
}

/**
 * Where a shared user lands after the login with several roles, or after picking a role in the
 * header: the legacy `my-property` page until the switch; once it is live, the 3.0.0 archive of the
 * role's property. Without a property the legacy page stays, as today.
 *
 * The login with one role already sends a shared user to the property home, whose guest buttons
 * follow `guestArchiveLink` and `guestSearchLink`.
 *
 * @since 3.0.0
 *
 * @param lang The language segment of the address
 * @param role The `userRoles` entry the user is entering with
 * @param live Whether the switch is live: this build's, unless a test says otherwise
 */
export function sharedUserLanding(
  lang: string,
  role: unknown,
  live: boolean = isArchiveV3Live()
): string[] {
  const propertyId = live ? sharedRolePropertyId(role) : "";
  return propertyId
    ? [lang, "app", "properties", propertyId, "documentsV3"]
    : [lang, "app", "properties", "my-property"];
}

/**
 * «Archivio fascicolo documentale» of the guests (temporary and shared) on the property home: the
 * legacy dossier until the switch, the 3.0.0 one once it is live. The legacy link stays relative,
 * exactly as the template wrote it.
 *
 * @since 3.0.0
 */
export function guestArchiveLink(
  live: boolean,
  lang: string,
  propertyId: string
): string | string[] {
  return live
    ? ["/", lang, "app", "properties", propertyId, "documentsV3"]
    : `../../${propertyId}/documents`;
}

/**
 * «Ricerca documenti» of the guests on the property home: the legacy search until the switch, the
 * 3.0.0 one once it is live. The legacy link stays relative, exactly as the template wrote it.
 *
 * @since 3.0.0
 */
export function guestSearchLink(
  live: boolean,
  lang: string,
  propertyId: string
): string | string[] {
  return live
    ? ["/", lang, "app", "properties", propertyId, "searchV3"]
    : `../../${propertyId}/search`;
}

/**
 * Whether the guests' «Ricerca documenti» is offered on the property home. Until the switch always,
 * as today. Once it is live, only with what the 3.0.0 search route asks — canReadDocumentsList AND
 * canSearchDocuments — so that the button never leads to a refusal: temporary roles never hold the
 * second, and no longer see it.
 *
 * @since 3.0.0
 */
export function showsGuestSearch(
  live: boolean,
  permissions: string[] | null | undefined
): boolean {
  if (!live) {
    return true;
  }
  const held = Array.isArray(permissions) ? permissions : [];
  return held.indexOf("canReadDocumentsList") > -1 && held.indexOf("canSearchDocuments") > -1;
}

/**
 * Role types offered «Inserisci nuovo documento» once the switch is live: the ones of the bar of the
 * 3.0.0 pages (`INSERT_ROLE_TYPES` of document-archive-nav.utils), the only ones the 3.0.0 insert page
 * serves.
 */
const STAFF_INSERT_ROLE_TYPES = ["admin", "operator", "master", "collaborator"];

/**
 * Whether «Ricerca documenti» of the staff block of the property home (every role but temporary and
 * shared) is offered. One 3.0.0 search page serves every role, so the rule is the guests' one
 * (@see showsGuestSearch), which is also the one of the bar of the 3.0.0 pages: until the switch
 * always, as today; once it is live, only with canReadDocumentsList AND canSearchDocuments, so that a
 * role with the switch «Ricerca documenti» off no longer sees it (FE-A1, Luis 23/10/2026).
 *
 * @since 3.0.0
 */
export function showsStaffSearch(
  live: boolean,
  permissions: string[] | null | undefined
): boolean {
  return showsGuestSearch(live, permissions);
}

/**
 * Whether «Inserisci nuovo documento» of the staff block of the property home is offered. Until the
 * switch always, as today. Once it is live, with the rule of the bar of the 3.0.0 pages (FE-A1, Luis
 * 23/10/2026): admin, operator, master and collaborator with the switch «Inserimento documenti
 * digitali» on (canCreateDigitalDocuments) and the two permissions the 3.0.0 insert route asks for
 * (canReadDocumentsList, canCreateDocuments), so that the button never leads to a refusal.
 *
 * The rule of the bar (`archiveNavButtons`) is written again here rather than imported: its file builds
 * on the insert rules, which would follow it into the initial bundle the property home ships in. The
 * spec holds the two together.
 *
 * @since 3.0.0
 */
export function showsStaffInsert(
  live: boolean,
  roleType: string | null | undefined,
  permissions: string[] | null | undefined
): boolean {
  if (!live) {
    return true;
  }
  const held = Array.isArray(permissions) ? permissions : [];
  return (
    typeof roleType === "string" &&
    STAFF_INSERT_ROLE_TYPES.indexOf(roleType) > -1 &&
    held.indexOf("canReadDocumentsList") > -1 &&
    held.indexOf("canCreateDocuments") > -1 &&
    held.indexOf("canCreateDigitalDocuments") > -1
  );
}
